<script setup lang="ts">
  import { ref, onMounted, computed, nextTick } from 'vue'
  import { useRoute } from 'vue-router'
  import {
    ArrowUpRight,
    Award,
    FileText,
    CheckCircle,
    XCircle,
    User,
    Building2,
    Landmark,
    Pencil,
    Check
  } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Card, CardContent } from '@/components/ui/card'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import AudioPlaybackButton from '@/components/app/calls/audioPlaybackButton.vue'
  import CallRating from '@/components/app/calls/callRatingModal.vue'
  import {
    CallsService,
    ClassificationEnum,
    CasesService,
    OrganisationsService,
    PhoneForwardingRulesService,
    ForwardStatusEnum,
    EntityTypeEnum,
    CompanyLegalFormEnum,
    type CustomerCall as Call,
    type CustomerCallResult,
    type CustomerOrganisation,
    type LawyerFeedback,
    type PhoneForwardingRule,
    type CustomerLawyerFeedback,
    type ShallowEntity,
    RoleEnum
  } from '@/types/generated'
  import VueMarkdown from 'vue-markdown-render'
  import { useTranslation } from '@/composables/useTranslation'
  import router from '@/router'
  import TrashIcon from '@/components/icons/TrashIcon.vue'
  import { ArrowLeftRight, ArrowRight } from 'lucide-vue-next'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { ROUTES } from '@/router/routes'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { GenericObject } from 'vee-validate'
  import * as yup from 'yup'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLawFieldStore } from '@/store/lawFieldStore'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'
  import { genericErrorToast } from '@/lib/toasts'

  const { t, locale } = useTranslation({
    en: {
      callFrom: 'Call from',
      existingClient: 'Existing Client',
      noFirstName: 'No First Name',
      noLastName: 'No Last Name',
      receivedOn: 'Received on',
      title: 'Title',
      contactDetails: 'Contact Details',
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone',
      noCaseInformation: 'No case information',
      additionalInformation: 'Additional Information',
      emailAddress: 'Email Address',
      noAdditionalInformation: 'No additional information provided',
      noEmail: 'No email provided',
      phoneNumber: 'Phone Number',
      noPhone: 'No phone number provided',
      name: 'Name',
      callDetails: 'Call Details',
      callContent: 'Call Content',
      yourRating: 'Your Rating',
      star: 'Star',
      stars: 'Stars',
      rateAIAssistant: 'Rate AI-Voice-Assistant',
      delete: 'Delete',
      convertToCase: 'Convert to case',
      toCase: 'To Case',
      ReceivedAt: 'Received on',
      notes: 'Notes',
      notesPlaceholder: 'Add internal notes...',
      clerk: 'Clerk',
      selectClerk: 'Select clerk...',
      callNotes: 'Call Notes & Assignment',
      lawField: 'Law Field',
      lawTopic: 'Law Topic',
      noClerkAssigned: 'Not assigned',
      connectedToCase: 'Connected to Case',
      caseNotes: 'Notes',
      caseClerk: 'Clerk',
      connectedToCaseTooltip: 'This field is directly connected to the case',
      emailFormatValid: 'Email format is valid and properly formatted',
      emailFormatInvalid:
        'AI extracted this email, but it appears to be invalid. Please check the audio snippet to verify the correct email address.',
      phoneFormatValid: 'Phone number format is valid and properly formatted',
      phoneFormatInvalid:
        'AI extracted this phone number, but it appears to be invalid. Please check the audio snippet to verify the correct phone number.',
      entityType: 'Entity Type',
      companyName: 'Company Name',
      legalForm: 'Legal Form',
      institutionName: 'Institution Name',
      street: 'Street',
      houseNumber: 'House Number',
      postalCode: 'Postal Code',
      city: 'City',
      contactPersonTitle: 'Contact Person Title',
      contactPersonFirstName: 'Contact Person First Name',
      contactPersonLastName: 'Contact Person Last Name',
      forwardedTo: 'Forwarded to',
      reasonForForwarding: 'Reason for Forwarding',
      forwardingSucceeded: 'Forwarding Succeeded',
      forwardingFailed: 'Forwarding Failed',
      tentativeEmail: 'Suggested Email (unverified)',
      tentativeEmailTooltip:
        'AI extracted this email but it may be incomplete. Edit to correct or click ✓ to confirm.',
      confirmEmailTooltip: 'Confirm or edit client email to proceed',
      notSelected: 'Not selected',
      titleMr: 'Mr',
      titleMs: 'Ms',
      titleDiverse: 'Diverse',
      naturalPerson: 'Natural Person',
      legalPerson: 'Legal Person',
      institution: 'Institution'
    },
    de: {
      callFrom: 'Anruf von',
      existingClient: 'Bestandsmandant',
      noFirstName: 'Kein Vorname',
      noLastName: 'Kein Nachname',
      receivedOn: 'Eingegangen am',
      title: 'Anrede',
      contactDetails: 'Kontaktdaten',
      firstName: 'Vorname',
      lastName: 'Nachname',
      phone: 'Telefon',
      noCaseInformation: 'Keine Fall Informationen',
      additionalInformation: 'Zusätzliche Informationen',
      emailAddress: 'E-Mail-Adresse',
      noAdditionalInformation: 'Keine zusätzlichen Informationen angegeben',
      noEmail: 'Keine E-Mail angegeben',
      phoneNumber: 'Telefonnummer',
      noPhone: 'Keine Telefonnummer angegeben',
      name: 'Name',
      callDetails: 'Fall Details',
      callContent: 'Inhalt des Anrufes',
      yourRating: 'Ihre Bewertung',
      star: 'Stern',
      stars: 'Sterne',
      rateAIAssistant: 'KI-Anrufbeantworter bewerten',
      delete: 'Löschen',
      convertToCase: 'In Anfrage konvertieren',
      toCase: 'Zur Anfrage',
      ReceivedAt: 'Angenommen am',
      notes: 'Notizen',
      notesPlaceholder: 'Interne Notizen hinzufügen...',
      clerk: 'Sachbearbeiter',
      selectClerk: 'Sachbearbeiter auswählen...',
      callNotes: 'Anruf Notizen & Zuweisung',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      noClerkAssigned: 'Nicht zugewiesen',
      connectedToCase: 'Mit Anfrage verbunden',
      caseNotes: 'Notizen',
      caseClerk: 'Sachbearbeiter',
      connectedToCaseTooltip: 'Dieses Feld ist direkt mit der Anfrage verbunden',
      emailFormatValid: 'E-Mail-Format ist gültig und korrekt formatiert',
      emailFormatInvalid:
        'Die KI hat diese E-Mail-Adresse extrahiert, aber sie scheint ungültig zu sein. Bitte überprüfen Sie das Audio-Snippet, um die korrekte E-Mail-Adresse zu verifizieren.',
      phoneFormatValid: 'Telefonnummer-Format ist gültig und korrekt formatiert',
      phoneFormatInvalid:
        'Die KI hat diese Telefonnummer extrahiert, aber sie scheint ungültig zu sein. Bitte überprüfen Sie das Audio-Snippet, um die korrekte Telefonnummer zu verifizieren.',
      entityType: 'Rechtliche Person',
      companyName: 'Firmenname',
      legalForm: 'Rechtsform',
      institutionName: 'Institutionsname',
      street: 'Straße',
      houseNumber: 'Hausnr.',
      postalCode: 'PLZ',
      city: 'Ort',
      contactPersonTitle: 'Kontaktperson Titel',
      contactPersonFirstName: 'Kontaktperson Vorname',
      contactPersonLastName: 'Kontaktperson Nachname',
      forwardedTo: 'Weitergeleitet an',
      reasonForForwarding: 'Grund für Weiterleitung',
      forwardingSucceeded: 'Weiterleitung Erfolgreich',
      forwardingFailed: 'Weiterleitung Fehlgeschlagen',
      tentativeEmail: 'Mögliche E-Mail (unbestätigt)',
      tentativeEmailTooltip:
        'Die KI hat diese E-Mail extrahiert, sie könnte aber unvollständig sein. Bearbeiten Sie sie oder klicken Sie ✓ zum Bestätigen.',
      confirmEmailTooltip: 'Bestätigen oder bearbeiten Sie die E-Mail-Adresse, um fortzufahren',
      notSelected: 'Nicht ausgewählt',
      titleMr: 'Herr',
      titleMs: 'Frau',
      titleDiverse: 'Divers',
      naturalPerson: 'Natürliche Person',
      legalPerson: 'Juristische Person',
      institution: 'Institution'
    },
    fr: {
      callFrom: 'Appel de',
      existingClient: 'Client existant',
      noFirstName: 'Pas de prénom',
      noLastName: 'Pas de nom',
      receivedOn: 'Reçu le',
      title: 'Civilité',
      contactDetails: 'Coordonnées',
      firstName: 'Prénom',
      lastName: 'Nom',
      phone: 'Téléphone',
      noCaseInformation: 'Aucune information sur le dossier',
      additionalInformation: 'Informations complémentaires',
      emailAddress: 'Adresse e-mail',
      noAdditionalInformation: 'Aucune information complémentaire fournie',
      noEmail: 'Aucune adresse e-mail fournie',
      phoneNumber: 'Numéro de téléphone',
      noPhone: 'Aucun numéro de téléphone fourni',
      name: 'Nom',
      callDetails: 'Détails du dossier',
      callContent: "Contenu de l'appel",
      yourRating: 'Votre évaluation',
      star: 'Étoile',
      stars: 'Étoiles',
      rateAIAssistant: "Évaluer l'assistant vocal IA",
      delete: 'Supprimer',
      convertToCase: 'Convertir en demande',
      toCase: 'Vers la demande',
      ReceivedAt: 'Reçu le',
      notes: 'Notes',
      notesPlaceholder: 'Ajouter des notes internes...',
      clerk: 'Collaborateur',
      selectClerk: 'Sélectionner un collaborateur...',
      callNotes: "Notes d'appel et attribution",
      lawField: 'Domaine juridique',
      lawTopic: 'Sujet juridique',
      noClerkAssigned: 'Non attribué',
      connectedToCase: 'Lié à une demande',
      caseNotes: 'Notes',
      caseClerk: 'Collaborateur',
      connectedToCaseTooltip: 'Ce champ est directement lié à la demande',
      emailFormatValid: "Le format de l'e-mail est valide et correctement formaté",
      emailFormatInvalid:
        "L'IA a extrait cette adresse e-mail, mais elle semble invalide. Veuillez vérifier l'extrait audio pour confirmer l'adresse e-mail correcte.",
      phoneFormatValid: 'Le format du numéro de téléphone est valide et correctement formaté',
      phoneFormatInvalid:
        "L'IA a extrait ce numéro de téléphone, mais il semble invalide. Veuillez vérifier l'extrait audio pour confirmer le numéro de téléphone correct.",
      entityType: 'Type de personne',
      companyName: "Nom de l'entreprise",
      legalForm: 'Forme juridique',
      institutionName: "Nom de l'institution",
      street: 'Rue',
      houseNumber: 'Numéro',
      postalCode: 'Code postal',
      city: 'Ville',
      contactPersonTitle: 'Civilité de la personne de contact',
      contactPersonFirstName: 'Prénom de la personne de contact',
      contactPersonLastName: 'Nom de la personne de contact',
      forwardedTo: 'Transféré à',
      reasonForForwarding: 'Motif du transfert',
      forwardingSucceeded: 'Transfert réussi',
      forwardingFailed: 'Transfert échoué',
      tentativeEmail: 'E-mail suggéré (non vérifié)',
      tentativeEmailTooltip:
        "L'IA a extrait cet e-mail mais il peut être incomplet. Modifiez-le ou cliquez sur ✓ pour confirmer.",
      confirmEmailTooltip: "Confirmez ou modifiez l'e-mail du client pour continuer",
      notSelected: 'Non sélectionné',
      titleMr: 'Monsieur',
      titleMs: 'Madame',
      titleDiverse: 'Divers',
      naturalPerson: 'Personne physique',
      legalPerson: 'Personne morale',
      institution: 'Institution'
    }
  })

  const route = useRoute()
  const params = route.params as { uuid: string }
  const userStore = useUserStore()
  const { fetchLawFields, getLawTopicOptions, getLawFieldOptions } = useLawFieldStore()
  const isVoiceStandAlone = ref(false)
  const call = ref<Call | null>(null)
  const lawyerFeedback = ref<CustomerLawyerFeedback | null>(null)
  const isRatingModalOpen = ref(false)
  const isCreatingCase = ref(false)
  const isEditingTentativeEmail = ref(false)
  const tentativeEmailInputRef = ref<HTMLInputElement | null>(null)
  const originalClientData = ref<ShallowEntity | null>(null)
  const isEmailFieldInvalid = ref(false)
  const forwardingRule = ref<PhoneForwardingRule | null>(null)

  const titleOptions = computed(() => [
    { value: '', title: t('notSelected') },
    { value: 'Herr', title: t('titleMr') },
    { value: 'Frau', title: t('titleMs') },
    { value: 'Divers', title: t('titleDiverse') }
  ])

  const entityTypeOptions = computed(() => [
    {
      value: EntityTypeEnum.NATURAL_PERSON,
      title: t('naturalPerson'),
      icon: User
    },
    {
      value: EntityTypeEnum.LEGAL_PERSON,
      title: t('legalPerson'),
      icon: Building2
    },
    {
      value: EntityTypeEnum.INSTITUTION,
      title: t('institution'),
      icon: Landmark
    }
  ])

  const legalFormOptions = computed(() => [
    { value: '', title: t('notSelected') },
    { value: CompanyLegalFormEnum.GMB_H, title: 'GmbH' },
    { value: CompanyLegalFormEnum['UG__HAFTUNGSBESCHRÄNKT_'], title: 'UG (haftungsbeschränkt)' },
    { value: CompanyLegalFormEnum.GMB_H___CO__KG, title: 'GmbH & Co. KG' },
    { value: CompanyLegalFormEnum.AG, title: 'AG' },
    { value: CompanyLegalFormEnum.AG___CO__KG, title: 'AG & Co. KG' },
    { value: CompanyLegalFormEnum.KG, title: 'KG' },
    { value: CompanyLegalFormEnum.OHG, title: 'OHG' },
    { value: CompanyLegalFormEnum.GBR, title: 'GbR' },
    { value: CompanyLegalFormEnum.PART_G, title: 'PartG' },
    { value: CompanyLegalFormEnum.PART_G_MB_B, title: 'PartG mbB' },
    { value: CompanyLegalFormEnum.KGA_A, title: 'KGaA' },
    { value: CompanyLegalFormEnum.E_G, title: 'eG' },
    { value: CompanyLegalFormEnum.E_V, title: 'eV' },
    { value: CompanyLegalFormEnum.EINZELUNTERNEHMEN, title: 'Einzelunternehmen' },
    { value: CompanyLegalFormEnum.STIFTUNG, title: 'Stiftung' },
    { value: CompanyLegalFormEnum.WEG, title: 'WEG' },
    { value: CompanyLegalFormEnum.SE, title: 'SE' },
    { value: CompanyLegalFormEnum.EWIV, title: 'EWIV' },
    { value: CompanyLegalFormEnum.LIMITED, title: 'Limited' },
    { value: CompanyLegalFormEnum.PLC, title: 'PLC' },
    { value: CompanyLegalFormEnum.SARL, title: 'SARL' },
    { value: CompanyLegalFormEnum.SAS, title: 'SAS' },
    { value: CompanyLegalFormEnum.SA__FR_, title: 'SA (FR)' },
    { value: CompanyLegalFormEnum.SCN, title: 'SCN' },
    { value: CompanyLegalFormEnum.S_A_R_L_, title: 'S.A.R.L.' },
    { value: CompanyLegalFormEnum.SRL, title: 'SRL' },
    { value: CompanyLegalFormEnum.SPA, title: 'SPA' },
    { value: CompanyLegalFormEnum.SL, title: 'SL' },
    { value: CompanyLegalFormEnum.SA__ES_, title: 'SA (ES)' },
    { value: CompanyLegalFormEnum.BV, title: 'BV' },
    { value: CompanyLegalFormEnum.NV, title: 'NV' },
    { value: CompanyLegalFormEnum.AB, title: 'AB' },
    { value: CompanyLegalFormEnum.OY, title: 'Oy' },
    { value: CompanyLegalFormEnum.AS, title: 'AS' },
    { value: CompanyLegalFormEnum.S_R_O_, title: 's.r.o.' },
    { value: CompanyLegalFormEnum.KFT_, title: 'Kft.' },
    { value: CompanyLegalFormEnum.SP__Z_O_O_, title: 'Sp. z o.o.' },
    { value: CompanyLegalFormEnum.OOO, title: 'OOO' }
  ])

  const defaultClientData = {
    first_name: '',
    last_name: '',
    title: '' as any,
    email: '',
    phone: '',
    street: '',
    street_number: '',
    zip: '',
    city: '',
    company_name: '',
    company_legal_form: '' as any,
    institution_name: ''
  }

  const defaultCallResult: CustomerCallResult = {
    uuid: '',
    classification: '' as ClassificationEnum,
    data: {
      client: {
        ...defaultClientData,
        entity_type: EntityTypeEnum.NATURAL_PERSON,
        is_company: '',
        changes: []
      },
      law: {
        field: '',
        topic: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      opponent: {
        entity_type: EntityTypeEnum.NATURAL_PERSON,
        is_company: '',
        changes: []
      }
    },
    inquiry: '',
    first_name_recording_start_seconds: null,
    first_name_recording_end_seconds: null,
    last_name_recording_start_seconds: null,
    last_name_recording_end_seconds: null,
    email_recording_start_seconds: null,
    email_recording_end_seconds: null,
    phone_number_recording_start_seconds: null,
    phone_number_recording_end_seconds: null
  }

  const fetchCall = async () => {
    const fetchedCall = (await CallsService.callsRetrieve({ uuid: params.uuid })) as Call
    call.value = {
      ...fetchedCall,
      result: {
        ...defaultCallResult,
        ...fetchedCall.result
      }
    }
    lawyerFeedback.value = fetchedCall.lawyer_feedback

    originalClientData.value = { ...fetchedCall.result.data.client }

    if (fetchedCall.forward && 'rule_uuid' in (fetchedCall.forward as any)) {
      try {
        // Note: We need to check if there's a service for fetching forwarding rules
        // For now, we'll use a placeholder approach
        console.log('Call was forwarded with rule_uuid:', (fetchedCall.forward as any).rule_uuid)
        // TODO: Implement actual API call to fetch forwarding rule
        forwardingRule.value = await PhoneForwardingRulesService.phoneForwardingRulesRetrieve({
          uuid: (fetchedCall.forward as any).rule_uuid
        })
      } catch (error) {
        console.error('Error fetching forwarding rule:', error)
      }
    }
  }
  const fetchCurrentOrg = async () => {
    const response = (await OrganisationsService.organisationsRetrieve({
      uuid: 'current'
    })) as CustomerOrganisation

    isVoiceStandAlone.value = response.enable_cases || false
  }

  const canShowFirstNameRecording = computed(() => {
    return (
      call.value?.audio_recording_url &&
      call.value?.phone_number?.can_record_calls &&
      call.value?.result?.first_name_recording_start_seconds !== null &&
      call.value?.result?.first_name_recording_end_seconds !== null
    )
  })

  const canShowLastNameRecording = computed(() => {
    return (
      call.value?.audio_recording_url &&
      call.value?.phone_number?.can_record_calls &&
      call.value?.result?.last_name_recording_start_seconds !== null &&
      call.value?.result?.last_name_recording_end_seconds !== null
    )
  })

  const canShowPhoneNumberRecording = computed(() => {
    return (
      call.value?.audio_recording_url &&
      call.value?.phone_number?.can_record_calls &&
      call.value?.result?.phone_number_recording_start_seconds !== null &&
      call.value?.result?.phone_number_recording_end_seconds !== null
    )
  })

  const canShowEmailRecording = computed(() => {
    return (
      call.value?.audio_recording_url &&
      call.value?.phone_number?.can_record_calls &&
      call.value?.result?.email_recording_start_seconds !== null &&
      call.value?.result?.email_recording_end_seconds !== null
    )
  })

  const createCase = async () => {
    if (!call.value) return

    isCreatingCase.value = true
    try {
      const response = await CallsService.callsCreateCaseCreate({ uuid: call.value.uuid })
      window.location.href = `/cases/${response.case_id}/`
    } catch (error) {
      console.error('Error creating case:', error)
      isCreatingCase.value = false
    }
  }

  const callToActionButton = computed(() => {
    if (!call.value) return null

    if (call.value.case_uuid) {
      return {
        text: t('toCase'),
        action: () =>
          (window.location.href = ROUTES.APP.CASES.LEGACY.CASE_DETAIL.replace(
            ':uuid',
            call.value!.case_uuid
          )),
        disabled: false
      }
    }

    return {
      text: t('convertToCase'),
      action: createCase,
      disabled: isCreatingCase.value
    }
  })

  const handleRatingClick = () => {
    isRatingModalOpen.value = true
  }

  const handleRatingUpdated = (newRating: string) => {
    if (lawyerFeedback.value) {
      lawyerFeedback.value = {
        ...lawyerFeedback.value,
        rating: newRating
      }
    }
    isRatingModalOpen.value = false
  }

  const deleteCall = async () => {
    if (call.value) {
      await CallsService.callsDestroy({ uuid: call.value.uuid })
      await router.push(ROUTES.APP.CALLS.ROOT)
    }
  }

  const localeMap: Record<string, string> = {
    en: 'en-GB',
    de: 'de-DE',
    fr: 'fr-FR'
  }

  const formatCallDate = (dateString: string) => {
    const date = new Date(dateString)
    const dateLocale = localeMap[locale.value] || 'de-DE'
    return date.toLocaleString(dateLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const user = computed(() => userStore.user)

  const isPhoneAiUser = computed(() => user.value?.role === RoleEnum.PHONE_AI_USER)

  const clerkOptions = computed(() => {
    if (!user.value?.org?.users) return []
    return user.value.org.users
      .map((user) => ({
        value: user.uuid,
        title: `${user.first_name} ${user.last_name}`.trim() || user.email
      }))
      .sort((a, b) => a.title.localeCompare(b.title))
  })

  // Remove read-only mode - all fields should be editable regardless of connection status
  // When connected to case, we'll just update the case data instead of call data

  const lawFieldOptions = computed<Option[]>(() => {
    return getLawFieldOptions(user.value)
  })

  const lawTopicOptions = computed<Option[]>(() => {
    const selectedField = selectedLawField.value
    if (!selectedField) {
      return []
    }
    return getLawTopicOptions(selectedField)
  })

  const isConnectedToCase = computed(() => {
    return call.value?.case?.uuid !== undefined && call.value?.case?.uuid !== null
  })

  const selectedClerks = computed({
    get() {
      if (isConnectedToCase.value) {
        const caseClerks = (call.value?.case as any)?.clerks
        if (caseClerks && caseClerks.length > 0) {
          return caseClerks.map((c: any) => c.uuid)
        }
        // Fallback to single clerk
        return call.value?.case?.clerk ? [call.value.case.clerk.uuid] : []
      }
      const callClerks = (call.value as any)?.clerks
      if (callClerks && callClerks.length > 0) {
        return callClerks.map((c: any) => c.uuid)
      }
      // Fallback to single clerk
      return call.value?.clerk?.uuid ? [call.value.clerk.uuid] : []
    },
    set(_value: (string | number)[]) {
      // This will be handled by the form submission, not direct assignment
    }
  })

  const displayedNotes = computed({
    get() {
      if (isConnectedToCase.value) {
        return call.value?.case?.note || ''
      }
      return call.value?.note || ''
    },
    set(value: string) {
      // This will be handled by the form submission, not direct assignment
    }
  })

  const selectedLawField = computed({
    get() {
      if (isConnectedToCase.value) {
        return call.value?.case?.law_field || ''
      }
      return call.value?.result?.data?.law?.field || ''
    },
    set(value: string) {
      // This will be handled by the form submission, not direct assignment
    }
  })

  const selectedLawTopic = computed({
    get() {
      if (isConnectedToCase.value) {
        return call.value?.case?.law_topic || ''
      }
      return call.value?.result?.data?.law?.topic || ''
    },
    set(value: string) {
      // This will be handled by the form submission, not direct assignment
    }
  })

  const handleSave = async (values: GenericObject) => {
    if (!originalClientData.value || !call.value) return

    if (values.phone !== originalClientData.value.phone) {
      call.value.result.data.client.is_valid_phone_number = null
    }
    if (values.email !== originalClientData.value.email) {
      call.value.result.data.client.is_valid_email = null
    }

    await CallsService.callsPartialUpdate({
      uuid: call.value!.uuid!,
      requestBody: { result: { data: { client: values } } }
    })
  }

  const handleLawAndClerkSave = async (values: GenericObject) => {
    if (isConnectedToCase.value) {
      await handleCaseUpdate(values)
      return
    }

    try {
      // Handle clerk update
      if (values.clerk !== undefined) {
        const clerkIds = Array.isArray(values.clerk)
          ? values.clerk
          : values.clerk
            ? [values.clerk]
            : []
        await CallsService.callsPartialUpdate({
          uuid: call.value!.uuid!,
          requestBody: {
            clerk_ids: clerkIds
          }
        })

        // Update local state to reflect the change
        if (call.value) {
          const newClerks = clerkIds
            .map((id: string) => user.value?.org?.users?.find((u) => u.uuid === id))
            .filter(Boolean)
          call.value = {
            ...call.value,
            clerk: newClerks[0] || null,
            clerks: newClerks
          } as typeof call.value
        }
      }

      if (values.lawField !== undefined || values.lawTopic !== undefined) {
        // Create minimal update payload focusing only on the specific law fields being changed
        const lawData: Record<string, string> = {}

        if (values.lawField !== undefined) {
          lawData.field = values.lawField
        }

        if (values.lawTopic !== undefined) {
          const options = getLawTopicOptions(values.lawField || call.value!.result.data.law.field)
          if (!options.find((option) => option.value === values.lawTopic)) {
            lawData.topic = ''
          } else {
            lawData.topic = values.lawTopic
          }
        }

        await CallsService.callsPartialUpdate({
          uuid: call.value!.uuid!,
          requestBody: {
            result: {
              data: {
                law: lawData
              }
            }
          }
        })

        if (call.value && call.value.result?.data?.law) {
          const updatedLaw = { ...call.value.result.data.law }
          if (values.lawField !== undefined) {
            updatedLaw.field = values.lawField
          }
          if (lawData.topic !== undefined) {
            updatedLaw.topic = lawData.topic
          }
          call.value = {
            ...call.value,
            result: {
              ...call.value.result,
              data: {
                ...call.value.result.data,
                law: updatedLaw
              }
            }
          }
        }
      }
    } catch (error) {
      genericErrorToast()
      await fetchCall()
    }
  }

  const handleNotesOnlySave = async (values: GenericObject) => {
    // If connected to case, update case data instead of call data
    if (isConnectedToCase.value) {
      await handleCaseUpdate(values)
      return
    }

    await CallsService.callsPartialUpdate({
      uuid: call.value!.uuid!,
      requestBody: {
        note: values.note
      }
    })
  }

  const handleCaseUpdate = async (values: GenericObject) => {
    if (!call.value?.case?.uuid) {
      genericErrorToast()
      return
    }

    try {
      const requestBody: Record<string, unknown> = {}

      if (values.note !== undefined) {
        requestBody.note = values.note
      }

      if (values.clerk !== undefined) {
        const clerkIds = Array.isArray(values.clerk)
          ? values.clerk
          : values.clerk
            ? [values.clerk]
            : []
        requestBody.clerk_ids = clerkIds
      }

      if (values.lawField !== undefined || values.lawTopic !== undefined) {
        requestBody.data = { law: {} }

        if (values.lawField !== undefined) {
          requestBody.data.law.field = values.lawField
        }

        if (values.lawTopic !== undefined) {
          const options = getLawTopicOptions(values.lawField || selectedLawField.value)
          if (!options.find((option) => option.value === values.lawTopic)) {
            requestBody.data.law.topic = ''
          } else {
            requestBody.data.law.topic = values.lawTopic
          }
        }
      }

      await CasesService.casesPartialUpdate({
        uuid: call.value.case.uuid,
        requestBody
      })

      if (call.value?.case) {
        const updatedCase = { ...call.value.case }

        if (values.note !== undefined) {
          updatedCase.note = values.note
        }
        if (values.clerk !== undefined) {
          const clerkIds = Array.isArray(values.clerk)
            ? values.clerk
            : values.clerk
              ? [values.clerk]
              : []
          const newClerks = clerkIds
            .map((id: string) => user.value?.org?.users?.find((u) => u.uuid === id))
            .filter(Boolean)
          updatedCase.clerk = newClerks[0] || null
          ;(updatedCase as Record<string, unknown>).clerks = newClerks
        }
        if (values.lawField !== undefined) {
          updatedCase.law_field = values.lawField
        }
        if (requestBody.data?.law?.topic !== undefined) {
          updatedCase.law_topic = requestBody.data.law.topic
        }

        call.value = { ...call.value, case: updatedCase }
      }
    } catch (error) {
      genericErrorToast()
      // Refresh call data to ensure consistency
      await fetchCall()
    }
  }

  const handleEmailValidationState = (state: { isInvalid: boolean }) => {
    isEmailFieldInvalid.value = state.isInvalid
  }

  const hasUnconfirmedEmail = computed(() => {
    return call.value?.result.data.client.tentative_email && !call.value?.result.data.client.email
  })

  const startEditingTentativeEmail = () => {
    isEditingTentativeEmail.value = true
    nextTick(() => {
      tentativeEmailInputRef.value?.focus()
      tentativeEmailInputRef.value?.select()
    })
  }

  const confirmTentativeEmail = async () => {
    if (call.value?.result.data.client.tentative_email) {
      const confirmedEmail = call.value.result.data.client.tentative_email
      call.value.result.data.client.email = confirmedEmail
      call.value.result.data.client.tentative_email = ''
      isEditingTentativeEmail.value = false

      await CallsService.callsPartialUpdate({
        uuid: params.uuid,
        requestBody: {
          result: {
            data: {
              client: {
                email: confirmedEmail,
                tentative_email: ''
              }
            }
          }
        } as any
      })
    }
  }

  const handleConvertToCase = () => {
    createCase()
  }

  const isLegalPerson = computed(() => {
    return call.value?.result.data.client.entity_type === EntityTypeEnum.LEGAL_PERSON
  })

  const isInstitution = computed(() => {
    return call.value?.result.data.client.entity_type === EntityTypeEnum.INSTITUTION
  })

  const titleLabel = computed(() => {
    if (isLegalPerson.value) return t('contactPersonTitle')
    return t('title')
  })

  const firstNameLabel = computed(() => {
    if (isLegalPerson.value) return t('contactPersonFirstName')
    return t('firstName')
  })

  const lastNameLabel = computed(() => {
    if (isLegalPerson.value) return t('contactPersonLastName')
    return t('lastName')
  })

  const getForwardedPhoneNumber = () => {
    if (!call.value?.forward || !('rule_uuid' in (call.value.forward as any))) return ''

    return forwardingRule.value?.target_phone_number || ''
  }

  const getForwardingReason = () => {
    if (!call.value?.forward || !('rule_uuid' in (call.value.forward as any))) return ''
    if (!forwardingRule.value) return 'Forwarding rule applied'

    const rule = forwardingRule.value
    const forwardData = call.value.forward as any
    const title = rule.title || ''
    const reason = forwardData.reason || ''

    // Combine title and reason if both exist
    if (title && reason) {
      return `${title}: ${reason}`
    } else if (title) {
      return title
    } else if (reason) {
      return reason
    } else {
      // Fallback based on rule type
      const ruleType = rule.rule_type
      switch (ruleType) {
        case 'phonenumber':
          return 'Call forwarded based on caller phone number'
        case 'entity':
          return 'Call forwarded based on entity type detection'
        case 'text':
          return 'Call forwarded based on text context analysis'
        default:
          return 'Call forwarded by rule'
      }
    }
  }

  const getForwardingStatus = () => {
    if (!call.value?.forward || !('rule_uuid' in (call.value.forward as any))) return null
    return (call.value.forward as any).status
  }

  onMounted(async () => {
    await Promise.all([fetchCurrentOrg(), fetchCall(), userStore.fetchUser(), fetchLawFields()])
  })
</script>

<template>
  <SpaCompatComponent>
    <div
      v-if="call"
      class="flex min-h-screen flex-col"
    >
      <div class="legacy-bg-color flex flex-grow items-start justify-center">
        <div class="container px-0 py-2">
          <!-- Header -->
          <div
            class="flex flex-row flex-wrap items-start justify-between border-b border-gray-200 pb-4 pt-4
              md:flex-nowrap"
          >
            <div class="ml-0 mt-2 h-auto grow">
              <div
                id="title"
                class="position-relative align-items-center justify-content-start flex gap-3"
              >
                <h1 class="text-h1 font-weight-bold text-nowrap">
                  {{ t('callFrom') }} {{ call.result.data.client.first_name || t('noFirstName') }}
                  {{ call.result.data.client.last_name || t('noLastName') }}
                </h1>
              </div>
              <p class="text-sm text-stone-500">
                {{ t('ReceivedAt') }} {{ formatCallDate(call.call_start_time) }}
              </p>
            </div>
            <div
              id="options"
              class="my-3 flex h-full items-start justify-end gap-2"
            >
              <Button
                variant="ghost"
                size="icon"
                class="h-9 min-w-10 border border-gray-300 bg-white text-purple-500 hover:bg-gray-50"
                :disabled="lawyerFeedback !== null"
                @click="handleRatingClick"
              >
                <Award class="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                class="h-9 min-w-10 border border-gray-300 bg-white text-red-500 hover:bg-gray-50"
                @click="deleteCall"
              >
                <TrashIcon class="h-4 w-4" />
              </Button>
              <template v-if="isVoiceStandAlone && !isPhoneAiUser">
                <!-- DISABLED: when tentative email exists (not yet confirmed) -->
                <TooltipProvider v-if="hasUnconfirmedEmail">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          disabled
                          variant="default"
                          class="h-9 cursor-not-allowed bg-purple-300 text-white"
                        >
                          <ArrowUpRight class="mr-2 h-4 w-4" />
                          {{ call.case_uuid ? t('toCase') : t('convertToCase') }}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      variant="secondary"
                      :showArrow="true"
                      side="top"
                    >
                      {{ t('confirmEmailTooltip') }}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <!-- Already connected to case - navigate -->
                <Button
                  v-else-if="call.case_uuid"
                  variant="default"
                  class="h-9 bg-purple-600 text-white hover:bg-purple-700"
                  @click="callToActionButton?.action"
                >
                  <ArrowUpRight class="mr-2 h-4 w-4" />
                  {{ t('toCase') }}
                </Button>
                <!-- Convert to case -->
                <Button
                  v-else
                  variant="default"
                  class="h-9 bg-purple-600 text-white hover:bg-purple-700"
                  :disabled="isCreatingCase"
                  @click="handleConvertToCase"
                >
                  <ArrowUpRight class="mr-2 h-4 w-4" />
                  {{ t('convertToCase') }}
                </Button>
              </template>
            </div>
          </div>

          <!-- Law Field, Law Topic, and Clerk Selection -->
          <div class="pt-2">
            <AutoSavingForm
              :debounceDelay="1500"
              :throttleInterval="2000"
              @save="handleLawAndClerkSave"
            >
              <div class="flex flex-row flex-wrap gap-3">
                <div class="mb-3 flex-1">
                  <div class="form-group">
                    <label class="flex items-center text-sm font-medium text-gray-800">
                      {{ t('lawField') }}
                      <TooltipProvider v-if="isConnectedToCase">
                        <Tooltip>
                          <TooltipTrigger>
                            <span
                              class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600
                                transition-colors hover:bg-gray-200"
                            >
                              <ArrowLeftRight class="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {{ t('connectedToCaseTooltip') }}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <SelectField
                      v-model="selectedLawField"
                      name="lawField"
                      :options="lawFieldOptions"
                      :label="''"
                      :rules="yup.string().notRequired()"
                    />
                  </div>
                </div>
                <div class="mb-3 flex-1">
                  <div class="form-group">
                    <label class="flex items-center text-sm font-medium text-gray-800">
                      {{ t('lawTopic') }}
                      <TooltipProvider v-if="isConnectedToCase">
                        <Tooltip>
                          <TooltipTrigger>
                            <span
                              class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600
                                transition-colors hover:bg-gray-200"
                            >
                              <ArrowLeftRight class="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {{ t('connectedToCaseTooltip') }}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <SelectField
                      v-model="selectedLawTopic"
                      name="lawTopic"
                      :options="lawTopicOptions"
                      :label="''"
                      :rules="yup.string().notRequired()"
                    />
                  </div>
                </div>
                <div class="mb-3 flex-1">
                  <div class="form-group">
                    <label class="flex items-center text-sm font-medium text-gray-800">
                      {{ isConnectedToCase ? t('caseClerk') : t('clerk') }}
                      <TooltipProvider v-if="isConnectedToCase">
                        <Tooltip>
                          <TooltipTrigger>
                            <span
                              class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600
                                transition-colors hover:bg-gray-200"
                            >
                              <ArrowLeftRight class="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {{ t('connectedToCaseTooltip') }}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <MultiSelectField
                      v-model="selectedClerks"
                      name="clerk"
                      :options="clerkOptions"
                      :label="''"
                      :show-avatars="true"
                    />
                  </div>
                </div>
              </div>
            </AutoSavingForm>
          </div>

          <div class="grid gap-4 pt-4 sm:gap-6 lg:grid-cols-3">
            <!-- Contact Information -->
            <Card class="bg-white lg:col-span-1">
              <CardContent class="p-4 sm:p-6">
                <div class="flex items-center justify-between">
                  <h2 class="text-lg font-medium text-gray-900">{{ t('contactDetails') }}</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="text-gray-500 hover:text-gray-700"
                  >
                    <FileText class="h-4 w-4" />
                  </Button>
                </div>
                <AutoSavingForm @save="handleSave">
                  <div class="mt-6 space-y-5">
                    <div class="space-y-2">
                      <div class="flex items-center gap-2">
                        <SelectField
                          v-model="call.result.data.client.entity_type"
                          :label="t('entityType')"
                          :options="entityTypeOptions"
                          name="entity_type"
                          :rules="yup.string().notRequired()"
                          class="w-full"
                        />
                      </div>
                    </div>

                    <!-- Legal person specific fields -->
                    <div
                      v-if="isLegalPerson"
                      class="space-y-2"
                    >
                      <InputField
                        v-model="call.result.data.client.company_name"
                        :label="t('companyName')"
                        name="company_name"
                        :rules="yup.string().notRequired()"
                      >
                        <template #tooltipTrigger>
                          <ArrowLeftRight
                            v-if="isConnectedToCase"
                            class="h-3 w-3"
                          />
                        </template>
                        <template #tooltipContent>
                          {{ t('connectedToCaseTooltip') }}
                        </template>
                      </InputField>
                    </div>
                    <div
                      v-if="isLegalPerson"
                      class="space-y-2"
                    >
                      <label class="mb-0 flex items-center pb-0 text-sm font-medium text-gray-800">
                        {{ t('legalForm') }}
                        <TooltipProvider v-if="isConnectedToCase">
                          <Tooltip>
                            <TooltipTrigger>
                              <span
                                class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600
                                  transition-colors hover:bg-gray-200"
                              >
                                <ArrowLeftRight class="h-3 w-3" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {{ t('connectedToCaseTooltip') }}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </label>
                      <SelectField
                        v-model="call.result.data.client.company_legal_form"
                        :label="''"
                        :options="legalFormOptions"
                        name="company_legal_form"
                        :rules="yup.string().notRequired()"
                      />
                    </div>

                    <!-- Institution specific fields -->
                    <div
                      v-if="isInstitution"
                      class="space-y-2"
                    >
                      <InputField
                        v-model="call.result.data.client.institution_name"
                        :label="t('institutionName')"
                        name="institution_name"
                        :rules="yup.string().notRequired()"
                      >
                        <template #tooltipTrigger>
                          <ArrowLeftRight
                            v-if="isConnectedToCase"
                            class="h-3 w-3"
                          />
                        </template>
                        <template #tooltipContent>
                          {{ t('connectedToCaseTooltip') }}
                        </template>
                      </InputField>
                    </div>
                    <div
                      v-if="!isInstitution"
                      class="space-y-1"
                    >
                      <label class="mb-0 flex items-center pb-0 text-sm font-medium text-gray-800">
                        {{ t('title') }}
                        <TooltipProvider v-if="isConnectedToCase">
                          <Tooltip>
                            <TooltipTrigger>
                              <span
                                class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600
                                  transition-colors hover:bg-gray-200"
                              >
                                <ArrowLeftRight class="h-3 w-3" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {{ t('connectedToCaseTooltip') }}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </label>
                      <SelectField
                        v-model="call.result.data.client.title"
                        :options="titleOptions"
                        :placeholder="t('notSelected')"
                        name="title"
                        :rules="yup.string().notRequired()"
                      />
                    </div>

                    <div
                      v-if="!isInstitution"
                      class="space-y-1"
                    >
                      <div class="flex items-end gap-2">
                        <div class="min-w-0 flex-1">
                          <div class="space-y-1">
                            <InputField
                              v-model="call.result.data.client.first_name"
                              name="first_name"
                              :label="firstNameLabel"
                              :rules="yup.string().notRequired()"
                            >
                              <template #tooltipTrigger>
                                <ArrowLeftRight
                                  v-if="isConnectedToCase"
                                  class="h-3 w-3"
                                />
                              </template>
                              <template #tooltipContent>
                                {{ t('connectedToCaseTooltip') }}
                              </template>
                            </InputField>
                          </div>
                        </div>
                        <AudioPlaybackButton
                          v-if="canShowFirstNameRecording"
                          :audioUrl="call.audio_recording_url!"
                          :endTime="call.result.first_name_recording_end_seconds!"
                          :startTime="call.result.first_name_recording_start_seconds!"
                        />
                      </div>
                    </div>

                    <div
                      v-if="!isInstitution"
                      class="space-y-1"
                    >
                      <div class="flex items-end gap-2">
                        <div class="min-w-0 flex-1">
                          <div class="space-y-1">
                            <InputField
                              v-model="call.result.data.client.last_name"
                              name="last_name"
                              :label="lastNameLabel"
                              :rules="yup.string().notRequired()"
                            >
                              <template #tooltipTrigger>
                                <ArrowLeftRight
                                  v-if="isConnectedToCase"
                                  class="h-3 w-3"
                                />
                              </template>
                              <template #tooltipContent>
                                {{ t('connectedToCaseTooltip') }}
                              </template>
                            </InputField>
                          </div>
                        </div>

                        <AudioPlaybackButton
                          v-if="canShowLastNameRecording"
                          :audioUrl="call.audio_recording_url || ''"
                          :endTime="call.result.last_name_recording_end_seconds || ''"
                          :startTime="call.result.last_name_recording_start_seconds || ''"
                        />
                      </div>
                    </div>

                    <div
                      v-if="!hasUnconfirmedEmail"
                      class="space-y-1"
                    >
                      <div class="flex items-end gap-2">
                        <div class="min-w-0 flex-1">
                          <div class="relative space-y-1">
                            <InputField
                              v-model="call.result.data.client.email"
                              type="email"
                              name="email"
                              :label="t('emailAddress')"
                              :rules="yup.string().email().notRequired()"
                              @validation-state="handleEmailValidationState"
                            >
                              <template #tooltipTrigger>
                                <ArrowLeftRight
                                  v-if="isConnectedToCase"
                                  class="h-3 w-3"
                                />
                              </template>
                              <template #tooltipContent>
                                {{ t('connectedToCaseTooltip') }}
                              </template>
                            </InputField>
                            <div
                              v-if="
                                call.result.data.client.email &&
                                call.result.data.client.is_valid_email !== null &&
                                call.result.data.client.is_valid_email !== undefined &&
                                !isEmailFieldInvalid
                              "
                              class="absolute bottom-0 right-3 flex h-5 w-5 -translate-y-1/2 items-center justify-center pt-1"
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <CheckCircle
                                      v-if="call.result.data.client.is_valid_email"
                                      class="h-5 w-5 text-green-600"
                                    />
                                    <XCircle
                                      v-else
                                      class="h-5 w-5 text-red-600"
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent class="max-w-xs">
                                    {{
                                      call.result.data.client.is_valid_email
                                        ? `${t('emailFormatValid')}`
                                        : `${t('emailFormatInvalid')}`
                                    }}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                        </div>
                        <div class="flex items-center gap-2">
                          <AudioPlaybackButton
                            v-if="canShowEmailRecording"
                            :audioUrl="call.audio_recording_url || ''"
                            :endTime="call.result.email_recording_end_seconds || ''"
                            :startTime="call.result.email_recording_start_seconds || ''"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Tentative Email with AI Suggestion Styling (AI-75) -->
                    <div
                      v-if="
                        call.result.data.client.tentative_email && !call.result.data.client.email
                      "
                      class="space-y-1"
                    >
                      <label class="text-sm font-medium text-gray-700">
                        {{ t('emailAddress') }}
                      </label>
                      <div class="flex items-center gap-2">
                        <!-- AI Suggestion Container with dashed border -->
                        <div
                          class="flex h-10 min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-dashed border-purple-600
                            bg-purple-100 py-2 pl-2.5 pr-2 shadow-[0_2px_4px_0_rgba(0,0,0,0.04)]"
                        >
                          <!-- Input - text directly on purple background -->
                          <input
                            ref="tentativeEmailInputRef"
                            v-model="call.result.data.client.tentative_email"
                            type="text"
                            :readonly="!isEditingTentativeEmail"
                            class="min-w-0 flex-1 border-none bg-transparent text-sm text-gray-900 outline-none ring-0
                              focus:border-none focus:outline-none focus:ring-0"
                            :class="{ 'cursor-default': !isEditingTentativeEmail }"
                          />
                          <!-- Pencil (Edit) with Tooltip -->
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-purple-200 bg-white
                                    text-purple-600 hover:bg-purple-50"
                                  @click="startEditingTentativeEmail"
                                >
                                  <Pencil class="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent class="max-w-xs">
                                {{ t('tentativeEmailTooltip') }}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <!-- Checkmark (Confirm) -->
                          <button
                            type="button"
                            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-purple-200 bg-white
                              text-purple-600 hover:bg-purple-50"
                            @click="confirmTentativeEmail"
                          >
                            <Check class="h-4 w-4" />
                          </button>
                        </div>
                        <!-- Play Button -->
                        <AudioPlaybackButton
                          v-if="canShowEmailRecording"
                          :audioUrl="call.audio_recording_url || ''"
                          :endTime="call.result.email_recording_end_seconds || ''"
                          :startTime="call.result.email_recording_start_seconds || ''"
                        />
                      </div>
                    </div>

                    <div class="space-y-1">
                      <div class="flex items-end gap-2">
                        <div class="min-w-0 flex-1">
                          <div class="relative space-y-1">
                            <InputField
                              v-model="call.result.data.client.phone"
                              type="tel"
                              name="phone"
                              :label="t('phone')"
                              :rules="yup.string().notRequired()"
                            >
                              <template #tooltipTrigger>
                                <ArrowLeftRight
                                  v-if="isConnectedToCase"
                                  class="h-3 w-3"
                                />
                              </template>
                              <template #tooltipContent>
                                {{ t('connectedToCaseTooltip') }}
                              </template>
                            </InputField>
                            <div
                              v-if="
                                call.result.data.client.phone &&
                                call.result.data.client.is_valid_phone_number !== null &&
                                call.result.data.client.is_valid_phone_number !== undefined
                              "
                              class="absolute bottom-0 right-3 flex h-5 w-5 -translate-y-1/2 items-center justify-center pt-1"
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <CheckCircle
                                      v-if="call.result.data.client.is_valid_phone_number"
                                      class="h-5 w-5 text-green-600"
                                    />
                                    <XCircle
                                      v-else
                                      class="h-5 w-5 text-red-600"
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent class="max-w-xs">
                                    {{
                                      call.result.data.client.is_valid_phone_number
                                        ? `${t('phoneFormatValid')}`
                                        : `${t('phoneFormatInvalid')}`
                                    }}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                        </div>
                        <div class="flex items-center gap-2">
                          <AudioPlaybackButton
                            v-if="canShowPhoneNumberRecording"
                            :audioUrl="call.audio_recording_url || ''"
                            :endTime="call.result.phone_number_recording_end_seconds || ''"
                            :startTime="call.result.phone_number_recording_start_seconds || ''"
                          />
                        </div>
                      </div>
                    </div>
                    <!-- Address fields -->
                    <div class="space-y-1">
                      <div class="flex items-end gap-2">
                        <div class="w-2/3 min-w-0">
                          <InputField
                            v-model="call.result.data.client.street"
                            name="street"
                            :label="t('street')"
                            :rules="yup.string().notRequired()"
                          >
                            <template #tooltipTrigger>
                              <ArrowLeftRight
                                v-if="isConnectedToCase"
                                class="h-3 w-3"
                              />
                            </template>
                            <template #tooltipContent>
                              {{ t('connectedToCaseTooltip') }}
                            </template>
                          </InputField>
                        </div>
                        <div class="w-1/3 min-w-0">
                          <InputField
                            v-model="call.result.data.client.street_number"
                            name="street_number"
                            :label="t('houseNumber')"
                            :rules="yup.string().notRequired()"
                          >
                            <template #tooltipTrigger>
                              <ArrowLeftRight
                                v-if="isConnectedToCase"
                                class="h-3 w-3"
                              />
                            </template>
                            <template #tooltipContent>
                              {{ t('connectedToCaseTooltip') }}
                            </template>
                          </InputField>
                        </div>
                      </div>
                    </div>

                    <div class="space-y-1">
                      <div class="flex items-end gap-2">
                        <div class="w-1/3 min-w-0">
                          <InputField
                            v-model="call.result.data.client.zip"
                            name="zip"
                            :label="t('postalCode')"
                            :rules="yup.string().notRequired()"
                          >
                            <template #tooltipTrigger>
                              <ArrowLeftRight
                                v-if="isConnectedToCase"
                                class="h-3 w-3"
                              />
                            </template>
                            <template #tooltipContent>
                              {{ t('connectedToCaseTooltip') }}
                            </template>
                          </InputField>
                        </div>
                        <div class="w-2/3 min-w-0">
                          <InputField
                            v-model="call.result.data.client.city"
                            name="city"
                            :label="t('city')"
                            :rules="yup.string().notRequired()"
                          >
                            <template #tooltipTrigger>
                              <ArrowLeftRight
                                v-if="isConnectedToCase"
                                class="h-3 w-3"
                              />
                            </template>
                            <template #tooltipContent>
                              {{ t('connectedToCaseTooltip') }}
                            </template>
                          </InputField>
                        </div>
                      </div>
                    </div>
                  </div>
                </AutoSavingForm>

                <!-- Notes Field -->
                <AutoSavingForm
                  class="mt-6"
                  @save="handleNotesOnlySave"
                >
                  <div class="space-y-1">
                    <label class="mb-0 flex items-center pb-0 text-sm font-medium text-gray-800">
                      {{ isConnectedToCase ? t('caseNotes') : t('notes') }}
                      <TooltipProvider v-if="isConnectedToCase">
                        <Tooltip>
                          <TooltipTrigger>
                            <span
                              class="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600
                                transition-colors hover:bg-gray-200"
                            >
                              <ArrowLeftRight class="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {{ t('connectedToCaseTooltip') }}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </label>
                    <TextAreaField
                      v-model="displayedNotes"
                      name="note"
                      :placeholder="t('notesPlaceholder')"
                      :rules="yup.string().notRequired()"
                      class="min-h-[100px]"
                    />
                  </div>
                </AutoSavingForm>
                <!-- Forwarded to field - only show when call was forwarded -->
                <div
                  v-if="call.forward && 'rule_uuid' in (call.forward as any)"
                  class="space-y-2"
                >
                  <!-- Forwarded Phone Field with Status Indicator -->
                  <div class="relative">
                    <InputField
                      :model-value="getForwardedPhoneNumber()"
                      type="tel"
                      name="forwarded_phone"
                      :label="t('forwardedTo')"
                      readonly
                      disabled
                    />

                    <!-- Status Dot Indicator with Tooltip -->
                    <div
                      v-if="getForwardingStatus()"
                      class="absolute right-3 top-[72%] z-10 -translate-y-1/2"
                      :data-cy="'CallDetailViewForwardingStatus'"
                    >
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger as-child>
                            <div
                              class="h-3 w-3 cursor-pointer rounded-full border-2 border-white shadow-sm"
                              :class="{
                                'bg-green-500':
                                  getForwardingStatus() === ForwardStatusEnum.SUCCEEDED,
                                'bg-red-500': getForwardingStatus() === ForwardStatusEnum.FAILED
                              }"
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              {{
                                getForwardingStatus() === ForwardStatusEnum.SUCCEEDED
                                  ? t('forwardingSucceeded')
                                  : t('forwardingFailed')
                              }}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>

                  <!-- AI Reasoning Toast for forwarding reason -->
                  <div
                    class="mt-4 flex items-start gap-2 rounded-r-lg border-l-2 p-2"
                    :class="{
                      'border-green-600 bg-green-50':
                        getForwardingStatus() === ForwardStatusEnum.SUCCEEDED,
                      'border-red-600 bg-red-50': getForwardingStatus() === ForwardStatusEnum.FAILED
                    }"
                    :data-cy="'CallDetailViewForwardingReason'"
                  >
                    <ArrowRight
                      class="mt-0.5 h-5 w-5 flex-shrink-0"
                      :class="{
                        'text-green-600': getForwardingStatus() === ForwardStatusEnum.SUCCEEDED,
                        'text-red-600': getForwardingStatus() === ForwardStatusEnum.FAILED
                      }"
                    />
                    <div class="flex-1 space-y-1">
                      <div class="text-sm font-medium text-gray-900">
                        {{ t('reasonForForwarding') }}
                      </div>
                      <div
                        v-if="getForwardingReason()"
                        class="text-xs text-gray-600"
                      >
                        {{ getForwardingReason() }}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <!-- Call Content -->
            <Card class="bg-white lg:col-span-2">
              <CardContent class="p-4 sm:p-6">
                <h2 class="text-lg font-medium text-gray-900">{{ t('callContent') }}</h2>

                <div class="mt-6 space-y-6 sm:space-y-8">
                  <div class="">
                    <VueMarkdown
                      v-if="call.result.inquiry != ''"
                      :source="call.result.inquiry"
                    />
                    <div v-else>{{ t('noCaseInformation') }}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <!-- Rating Modal -->
        <CallRating
          v-if="call"
          :call-uuid="call.uuid"
          :is-open="isRatingModalOpen"
          @close="() => (isRatingModalOpen = false)"
          @rating-updated="handleRatingUpdated"
        />
      </div>
    </div>
  </SpaCompatComponent>
</template>

<style scoped>
  a:hover {
    text-decoration: none;
  }

  .legacy-bg-color {
    background-color: #f8fafc !important;
  }
</style>
